/**
 * CertificatesTab — the "Certificates" tab of the Accountability Registry App Pack.
 *
 * A roster of represented agents (reusing the registry projection, RBAC-scoped + DB-paged)
 * where each row opens its Accountability Certificate: exportable, per-agent proof of
 * continuous ownership from creation to now, with the hash-linked evidence chain RE-VERIFIED
 * at issue time. No fabricated proof — the chain verdict comes straight from the backend
 * (GET /ownership/agents/{id}/certificate → OwnershipRecordService.verifyChain).
 *
 * Roster: api.listAccountabilityRegistry. Certificate: api.getOwnershipCertificate.
 * Export writes the certificate JSON to the operator's machine (client-side, no upload).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../../services/api';
import {
  Card, Table, Pill, Button, EmptyState, Skeleton, Slideout,
} from '../../components/v2';
import { OwnerAvatar, TierBadge } from './AccountabilityUI';
import {
  BadgeCheck, ShieldCheck, ShieldAlert, HelpCircle, ChevronLeft, ChevronRight,
  Download, UserCheck, Link2,
} from 'lucide-react';

const PAGE_SIZE = 20;

function tierTone(tier) {
  switch (tier) {
    case 'TIER_1': return 'danger';
    case 'TIER_2': return 'warning';
    case 'TIER_3': return 'cyan';
    case 'TIER_4': return 'muted';
    default: return 'muted';
  }
}
function stateTone(state) {
  switch (state) {
    case 'ACTIVE': return 'success';
    case 'NOMINATED': return 'warning';
    case 'DELEGATED': return 'brand';
    case 'TRANSFER_PENDING':
    case 'SUCCESSION_PENDING': return 'action';
    case 'ORPHANED':
    case 'DISCOVERED_UNOWNED': return 'danger';
    case 'RETIRED': return 'muted';
    default: return 'muted';
  }
}
function ownerLabel(r) {
  if (r.ownershipState === 'ACTIVE' && r.ownerName) {
    return (<span className="inline-flex items-center gap-1.5"><OwnerAvatar name={r.ownerName} />{r.ownerName}</span>);
  }
  if (r.ownershipState === 'NOMINATED' && r.ownerName) {
    return (<span className="inline-flex items-center gap-1.5 text-v2-ink-muted"><OwnerAvatar name={r.ownerName} />Pending · {r.ownerName}</span>);
  }
  return <span className="text-v2-ink-muted">Not yet accepted</span>;
}
function attestationTone(status) {
  switch (status) {
    case 'CURRENT': return 'success';
    case 'STALE': return 'warning';
    case 'LAPSED': return 'danger';
    case 'NONE': return 'danger';
    default: return 'muted';
  }
}
function CertRow({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-[12px] border-t border-v2-border-subtle first:border-t-0">
      <span className="text-v2-ink-muted">{k}</span>
      <span className="text-v2-ink font-medium text-right">{v}</span>
    </div>
  );
}
function CertCard({ r, onOpen, stateTone: st, ownerLabel }) {
  const unowned = r.ownershipState === 'DISCOVERED_UNOWNED' || r.ownershipState === 'ORPHANED' || !r.ownerName;
  return (
    <div className={`bg-v2-surface border rounded-v2-md shadow-v2-sm p-[15px] ${unowned ? 'border-v2-danger' : 'border-v2-border-subtle'}`}>
      <div className="flex items-center gap-2.5 mb-2.5">
        <span className={`inline-flex items-center justify-center w-[30px] h-[30px] rounded-full text-[10px] font-extrabold shrink-0 ${unowned ? 'bg-v2-danger-soft text-v2-danger' : 'bg-v2-action-soft text-v2-action'}`}>{unowned ? '!' : 'P7'}</span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-bold text-v2-ink truncate">{r.agentName || 'Unnamed agent'}</div>
          <div className="text-[10px] text-v2-ink-muted font-mono truncate">{r.agentId ? `${r.agentId.slice(0, 8)}…` : '—'}</div>
        </div>
        {r.ownershipState
          ? <Pill tone={st(r.ownershipState)} size="sm" dot>{r.ownershipStateLabel || r.ownershipState}</Pill>
          : null}
      </div>
      <CertRow k="Accountable owner" v={ownerLabel(r)} />
      <CertRow k="Materiality" v={r.materialityTier
        ? <TierBadge tier={r.materialityTier} label={r.materialityLabel} />
        : <span className="text-v2-ink-muted">unscored</span>} />
      <CertRow k="Attestation" v={r.attestationLabel
        ? <Pill tone={attestationTone(r.attestationStatus)} size="sm">{r.attestationLabel}</Pill>
        : <span className="text-v2-ink-muted">—</span>} />
      <div className="mt-3 flex justify-end">
        <Button variant={unowned ? 'secondary' : 'primary'} size="sm" iconLeft={<BadgeCheck size={14} />} onClick={() => onOpen(r)}>
          View certificate
        </Button>
      </div>
    </div>
  );
}
function formatWhen(iso, withTime) {
  if (!iso) return '—';
  try {
    const opts = withTime
      ? { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
      : { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(iso).toLocaleString(undefined, opts);
  } catch (e) {
    return '—';
  }
}
function shortHash(h) {
  if (!h) return '—';
  return h.length > 16 ? `${h.slice(0, 10)}…${h.slice(-6)}` : h;
}
// apiFetch wraps success as { ok, data, etag }; unwrap to the body (tolerate a raw body).
const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

export default function CertificatesTab({ active = true }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null); // roster row being certified
  const [cert, setCert] = useState(null);
  const [certLoading, setCertLoading] = useState(false);
  const [certError, setCertError] = useState(null);

  const loadRoster = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = unwrap(await api.listAccountabilityRegistry({ page, size: PAGE_SIZE }));
      setRows(Array.isArray(body?.content) ? body.content : []);
      setTotal(typeof body?.totalElements === 'number' ? body.totalElements : 0);
    } catch (e) {
      setError(e?.message || 'Could not load the certificate roster.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  // Background refresh on re-activation (keep-alive keeps this tab mounted).
  const reactivatedRef = React.useRef(false);
  useEffect(() => {
    if (!active) return;
    if (!reactivatedRef.current) { reactivatedRef.current = true; return; }
    loadRoster();
  }, [active]);

  const openCertificate = useCallback(async (row) => {
    setSelected(row);
    setCert(null);
    setCertError(null);
    setCertLoading(true);
    try {
      const body = unwrap(await api.getOwnershipCertificate(row.agentId));
      setCert(body || null);
    } catch (e) {
      setCertError(e?.message || 'Could not issue the certificate.');
    } finally {
      setCertLoading(false);
    }
  }, []);

  const closeCertificate = useCallback(() => {
    setSelected(null);
    setCert(null);
    setCertError(null);
  }, []);

  const [exporting, setExporting] = useState(false);

  // Export the examiner-grade PDF — server-generated from the live certificate (chain re-verified
  // at issue), streamed as application/pdf. Cookie-authenticated GET; we download the blob.
  const exportCertificate = useCallback(async () => {
    if (!cert || !cert.agentId) return;
    setExporting(true);
    try {
      const res = await fetch(`/api/v1/ownership/agents/${encodeURIComponent(cert.agentId)}/certificate.pdf`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/pdf' },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = (cert.agentName || cert.agentId || 'agent').toString().replace(/[^a-z0-9._-]+/gi, '_');
      a.href = url;
      a.download = `accountability-certificate-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setCertError(e?.message || 'Could not export the certificate PDF.');
    } finally {
      setExporting(false);
    }
  }, [cert]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const chainBadge = cert && cert.represented
    ? (cert.chainValid
      ? <Pill tone="success" size="sm" dot icon={<ShieldCheck size={12} />}>Evidence chain verified</Pill>
      : <Pill tone="danger" size="sm" dot icon={<ShieldAlert size={12} />}>Chain broken @ seq {cert.chainFailedAtSeq}</Pill>)
    : null;

  const timelineColumns = useMemo(() => ([
    { key: 'seq', header: '#', render: (e) => <span className="text-v2-ink-muted tabular-nums">{e.seq}</span> },
    { key: 'type', header: 'Event', render: (e) => <span className="text-v2-ink">{(e.type || '').replace(/_/g, ' ')}</span> },
    { key: 'at', header: 'When', render: (e) => <span className="text-v2-ink-muted">{formatWhen(e.at, true)}</span> },
    { key: 'actor', header: 'Actor', render: (e) => <span className="text-v2-ink-muted">{e.actorName || '—'}</span> },
  ]), []);

  return (
    <>
      {loading && rows.length === 0 ? (
        <Card padding="none"><div className="p-3"><Skeleton variant="text" lines={8} /></div></Card>
      ) : error ? (
        <Card padding="none"><EmptyState
          icon={<HelpCircle size={22} />}
          title="Could not load certificates"
          description={error}
          primaryAction={<Button variant="secondary" size="sm" onClick={loadRoster}>Try again</Button>}
        /></Card>
      ) : rows.length === 0 ? (
        <Card padding="none"><EmptyState
          icon={<BadgeCheck size={22} />}
          title="No certificates yet"
          description="Certificates are issued for represented agents. Represent the estate from Outcomes & Actions to bind agents to an accountable owner, then their certificates appear here."
        /></Card>
      ) : (
        <>
          <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
            {rows.map((r, i) => (
              <CertCard key={`${r.agentId || 'row'}:${i}`} r={r} onOpen={openCertificate} stateTone={stateTone} ownerLabel={ownerLabel} />
            ))}
          </div>
          <div className="flex items-center justify-between mt-4">
            <div className="text-[12px] text-v2-ink-muted">
              {`${total} represented agent${total === 1 ? '' : 's'}`}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 0} iconLeft={<ChevronLeft size={14} />}
                onClick={() => setPage((p) => Math.max(0, p - 1))}>Prev</Button>
              <span className="text-[12px] text-v2-ink-muted">{`Page ${page + 1} of ${totalPages}`}</span>
              <Button variant="ghost" size="sm" disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}>Next<ChevronRight size={14} /></Button>
            </div>
          </div>
        </>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeCertificate}
        width={560}
        ariaLabel="Accountability certificate"
        header={(
          <div className="flex items-center gap-2">
            <BadgeCheck size={18} className="text-v2-trust" />
            <div>
              <div className="text-[14px] font-semibold text-v2-ink">Accountability Certificate</div>
              <div className="text-[12px] text-v2-ink-muted">{selected?.agentName || 'Agent'}</div>
            </div>
          </div>
        )}
      >
        {certLoading ? (
          <div className="p-2"><Skeleton variant="text" lines={10} /></div>
        ) : certError ? (
          <EmptyState icon={<HelpCircle size={22} />} title="Could not issue certificate" description={certError} />
        ) : !cert ? null : !cert.represented ? (
          <EmptyState
            icon={<ShieldAlert size={22} />}
            title="No ownership record"
            description="This agent has no ownership record yet, so there is no continuous-ownership proof to certify. Represent the estate to nominate an accountable owner."
          />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {chainBadge}
              {!cert.ownerPrincipalId
                ? <Pill tone="muted" size="sm">Owner not yet accepted</Pill>
                : cert.independentOfCreator
                  ? <Pill tone="success" size="sm" icon={<UserCheck size={12} />}>Owner independent of creator</Pill>
                  : <Pill tone="warning" size="sm">Owner = creator</Pill>}
              {cert.materialityTier
                ? <Pill tone={tierTone(cert.materialityTier)} size="sm">{cert.materialityTier.replace('TIER_', 'Tier ')}</Pill>
                : null}
            </div>

            <Card padding="sm">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
                <dt className="text-v2-ink-muted">Accountable owner</dt>
                <dd className="text-v2-ink text-right">{cert.ownerName || 'Not yet accepted'}</dd>
                <dt className="text-v2-ink-muted">Creator (provenance)</dt>
                <dd className="text-v2-ink text-right">{cert.creatorName || '—'}</dd>
                <dt className="text-v2-ink-muted">Lifecycle state</dt>
                <dd className="text-right">{cert.ownershipState
                  ? <Pill tone={stateTone(cert.ownershipState)} size="sm" dot>{cert.ownershipState}</Pill> : '—'}</dd>
                <dt className="text-v2-ink-muted">Rubric version</dt>
                <dd className="text-v2-ink text-right">{cert.rubricVersion || '—'}</dd>
                <dt className="text-v2-ink-muted">Accountability since</dt>
                <dd className="text-v2-ink text-right">{formatWhen(cert.recordCreatedAt)}</dd>
                <dt className="text-v2-ink-muted">Last event</dt>
                <dd className="text-v2-ink text-right">{formatWhen(cert.lastEventAt, true)}</dd>
                <dt className="text-v2-ink-muted flex items-center gap-1"><Link2 size={12} /> Chain head</dt>
                <dd className="text-v2-ink text-right font-mono text-[11px]" title={cert.chainHeadHash || ''}>{shortHash(cert.chainHeadHash)}</dd>
                <dt className="text-v2-ink-muted">Issued</dt>
                <dd className="text-v2-ink text-right">{formatWhen(cert.issuedAt, true)}</dd>
              </dl>
            </Card>

            <div>
              <div className="text-[12px] font-semibold text-v2-ink-muted mb-1.5">
                {`Ownership evidence chain · ${cert.eventCount} event${cert.eventCount === 1 ? '' : 's'}`}
              </div>
              <Card padding="none">
                {Array.isArray(cert.timeline) && cert.timeline.length > 0 ? (
                  <Table columns={timelineColumns} rows={cert.timeline}
                    rowKey={(e, i) => `${e.seq}:${i}`} ariaLabel="Ownership evidence chain" />
                ) : (
                  <div className="p-3 text-[12.5px] text-v2-ink-muted">No events recorded on this chain.</div>
                )}
              </Card>
            </div>

            <div className="flex justify-end">
              <Button variant="primary" size="sm" iconLeft={<Download size={15} />} loading={exporting} onClick={exportCertificate}>
                Export PDF
              </Button>
            </div>
          </div>
        )}
      </Slideout>
    </>
  );
}
